// import { unflatten } from 'flat';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
// import moment from 'moment-timezone';
import MatTableList from '../../../../../shared/tables/materialTable/MatTableList';
import UserPropertyType from '../../components/custom';
import CreatorInList from './CreatorInList';
import ApprovalStatus from './ApprovalStatus';
import MaxAmount from './MaxAmount';
import { formatIntNumber, formatCompactNumber, formatCurrency, dealCurrencyCode, formatDate } from '../../../../../shared/helpers/WVFormatter';
import { countriesBH } from '../../../../../shared/helpers/WVConstants';
import ActionCell from './ActionCell';

// Trinity's results have arrived, so a 0 from it is a real 0 rather than "never asked".
// Either the price-approvals webhook came back, or the daily stats job asked Trinity.
const trinityResolved = params => Boolean(
  params?.aiApprovalsData?.dateModified || params?.trinityData?.expiresAt,
);

const Table = React.memo(({
  action,
  resource,
  setTag,
  version,
  setSelectedIds,
  setCurrentProspect,
  setShowNotesModal,
}) => {
  const countries = useMemo(() => {
    const c = countriesBH();
    c.Unknown = 'Unknown';
    c.Other = 'Other';
    return c;
  }, []);

  const columns = useMemo(() => [
    {
      id: 'creatorName',
      Header: () => 'Creator Name',
      accessor: record => (record?.params?.creatorDisplayName || ''),
      Cell: p => (
        <CreatorInList
          record={p.row.original}
        />
      ),
      width: 350,
      className: 'd-flex align-items-center',
      isFreeze: true,
    },
    {
      id: 'status',
      Header: () => 'Approval Status',
      accessor: () => '',
      Cell: p => (
        <ApprovalStatus
          p={p}
          placeholder="Approval Status"
          notes={p.row.original.params?.brandNotes || ''}
          name="approvalsBrandStatus"
          noteName="brandNotes"
          setCurrentProspect={setCurrentProspect}
          setShowNotesModal={setShowNotesModal}
        />
      ),
      className: 'd-flex align-items-center',
      // sortable: false,
      width: 200,
      isFreeze: true,
    },
    {
      id: 'maxPrice',
      Header: () => 'Contract Amount',
      accessor: record => Number(record?.params?.dealObject?.dealTerms?.max_price || 0),
      Cell: p => (
        <MaxAmount
          record={p.row.original}
          updateRecordFunction={p.updateRecordFunction}
          setCurrentProspect={setCurrentProspect}
          setShowNotesModal={setShowNotesModal}
        />
      ),
      width: 200,
      className: 'd-flex align-items-center',
    },
    {
      id: 'dealJustification',
      Header: () => 'Justification',
      accessor: p => p.params.dealJustification,
      Cell: p => (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {p.value || '-- Not Assigned --'}
        </div>
      ),
      width: 200,
      className: 'd-flex align-items-center',
    },
    {
      id: 'aiApprovalsData.categories',
      Header: () => 'Category',
      Cell: (p) => {
        const { params } = p.row.original;
        const category = params.influencerCategory
          || params.aiApprovalsData?.categories?.[params.targetBrandName?.toLowerCase()]
          || '-';
        return (
          <div style={{ width: '100%' }}>
            {category}
          </div>
        );
      },
      width: 150,
      className: 'd-flex align-items-center',
    },
    {
      id: 'marketingCampaign',
      Header: () => 'Campaign',
      accessor: record => (record?.params?.dealObject?.dealTerms?.marketing_campaign || 'General'),
      width: 150,
      className: 'd-flex align-items-center',
    },
    {
      id: 'subscribers',
      Header: () => 'Subs',
      accessor: p => p.params.subscribers,
      Cell: p => formatCompactNumber(p.row.original.params.subscribers),
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'v30',
      Header: () => 'V30',
      // Prefer the negotiated deal-term V30, then the existing top-level v30 — many
      // deals (e.g. SimpliSafe) never get the deal term populated. Trinity's own
      // figure, filled by the daily stats job, is the last resort.
      accessor: record => (record?.params?.dealObject?.dealTerms?.v30_number_of_impressions || record?.params?.v30 || record?.params?.trinityData?.v30 || 0),
      Cell: (p) => {
        const { params } = p.row.original;
        const v30 = params.dealObject?.dealTerms?.v30_number_of_impressions || params.v30 || params.trinityData?.v30;
        if (v30) return formatIntNumber(v30);
        return trinityResolved(params) ? formatIntNumber(0) : '-';
      },
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'averageComments',
      Header: () => 'AVG Comments',
      accessor: p => p.params.aiApprovalsData?.averageComments,
      Cell: p => (p.row.original.params.aiApprovalsData?.averageComments != null ? formatIntNumber(p.row.original.params.aiApprovalsData?.averageComments) : '-'),
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'er30',
      Header: () => 'ER30',
      accessor: record => (record?.params?.er30 || 0),
      Cell: p => (p.row.original.params.er30 != null ? formatIntNumber(p.row.original.params.er30) : '-'),
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'percentUS',
      Header: () => 'US %',
      Cell: p => `${Number(p.row.original.params.percentUS || 0).toFixed(0)}%`,
      width: 70,
      className: 'd-flex align-items-center',
    },
    {
      id: 'femalePercentage',
      Header: () => 'Female %',
      Cell: p => `${Number(p.row.original.params.femalePercentage || 0).toFixed(0)}%`,
      width: 85,
      className: 'd-flex align-items-center',
    },
    {
      id: 'percent25plus',
      Header: () => '25+ %',
      Cell: p => `${Number(p.row.original.params.percent25plus || 0).toFixed(0)}%`,
      width: 70,
      className: 'd-flex align-items-center',
    },
    {
      id: 'cpm',
      Header: () => 'CPM',
      accessor: record => (record?.params?.cpm || 0),
      Cell: p => (
        <div
          style={{ width: '100%' }}
        >
          {formatCurrency(Number(p.row.original.params.cpm || 0), dealCurrencyCode(p.row.original.params.dealObject), 2, true)}
        </div>
      ),
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'createdAt',
      Header: () => 'Submit Date',
      accessor: p => p.params.createdAt,
      Cell: p => formatDate(p.row.original.params.createdAt),
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'reviewDate',
      Header: () => 'Review Date',
      // Computed by the resource's aggregation, which no longer sends the history (IL-7514).
      accessor: record => record?.params?.reviewDate || null,
      Cell: p => (p.value ? formatDate(p.value) : '-'),
      width: 120,
      className: 'd-flex align-items-center',
    },
    {
      id: 'countryOfResidence',
      Header: () => 'Country',
      accessor: p => p.params.influencerObject?.countryOfResidence,
      Cell: p => (
        <div>
          {p.value ? countries[p.value] : '-'}
        </div>
      ),
      width: 110,
      className: 'd-flex align-items-center',
    },
    {
      id: 'аctionPriceApproval',
      Header: '',
      accessor: () => null,
      maxWidth: 30,
      Cell: p => (
        <ActionCell
          record={p.row.original}
        />
      ),
      disableSortBy: true,
      className: 'd-flex align-items-center align-middle',
    },
  ], [countries, setCurrentProspect, setShowNotesModal]);

  return (
    <MatTableList
      action={action}
      resource={resource}
      setTag={setTag}
      version={version}
      UserPropertyType={UserPropertyType}
      showFilters={false}
      showTitle={false}
      sortBy="createdAt"
      direction="asc"
      useFlex
      v={2}
      noActions
      isResizable={false}
      useCheckboxes
      checkboxHandler={e => setSelectedIds(e)}
      useUnflatten
      customColumns={columns}
      enabledStyles={false}
    />
  );
}, (a, a1) => a.version === a1.version && a.searchTerms?.length === a1.searchTerms?.length);

Table.propTypes = {
  setTag: PropTypes.func.isRequired,
  action: PropTypes.objectOf(PropTypes.any).isRequired,
  resource: PropTypes.objectOf(PropTypes.any).isRequired,
  version: PropTypes.string.isRequired,
  setSelectedIds: PropTypes.func.isRequired,
  setCurrentProspect: PropTypes.func.isRequired,
  setShowNotesModal: PropTypes.func.isRequired,
};
export default Table;
